import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    StyleSheet
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type ITreeNode,
    DropdownMenu,
    ToggleGroup,
    TreeView,
    Button,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    FileIcon
} from "../../anatomy/icons";

const NAME = "TreeView";

const USAGE_CODE = `const nodes = [
    {
        children: [
            {
                title: "report.pdf",
                key: "report"
            }
        ],
        title: "Documents",
        key: "documents"
    }
];

<TreeView
    onSelect={(keys) => setSelectedKeys(keys)}
    defaultExpandedKeys={["documents"]}
    selectedKeys={selectedKeys}
    aria-label="Project files"
    selectionMode="single"
    isGuideVisible={true}
    nodes={nodes}
/>`;

const LAZY_CODE = `const handleLoadChildren = async (node: ITreeNode) => {
    const children = await fetchChildren(node.key);

    setNodes((current) => attachChildren(current, node.key, children));
};

<TreeView
    onLoadChildren={handleLoadChildren}
    aria-label="Project files"
    nodes={nodes}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const FILE_SIZES = {
    contract: "140 KB",
    proposal: "86 KB",
    cover: "2.4 MB",
    logo: "12 KB"
};

const PERMISSION_LEAVES = [
    "mobile",
    "website",
    "monthly",
    "yearly"
];

const INITIAL_EXPANDED = [
    "documents"
];

const INITIAL_SELECTED = [
    "report"
];

const LAZY_ROOT = [
    "workspace"
];

const LOAD_DELAY = 900;

const COLUMN_STYLE: CSSProperties = {
    alignItems: "flex-start",
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const CONTROLS_STYLE: CSSProperties = {
    justifyContent: "center",
    flexWrap: "wrap",
    display: "flex",
    gap: 12
};

const GRID_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
    display: "flex",
    gap: 24
};

const stylesheet = StyleSheet.create({
    tree: {
        width: 170
    },
    state: {
        width: 260
    }
});

const findNode = (nodes: Array<ITreeNode>, key: string): ITreeNode | undefined => {
    for(const node of nodes) {
        if(node.key === key) {
            return node;
        }

        const child = node.children ? findNode(node.children, key) : undefined;

        if(child) {
            return child;
        }
    }

    return undefined;
};

const getFileNodes = (t: DocsTranslate): Array<ITreeNode> => [
    {
        title: t("treeViewPage.folderDocuments"),
        children: [
            {
                meta: FILE_SIZES.proposal,
                title: "proposal.docx",
                icon: FileIcon,
                key: "proposal"
            },
            {
                title: t("sample.fileName"),
                meta: t("sample.fileSize"),
                icon: FileIcon,
                key: "report"
            },
            {
                meta: FILE_SIZES.contract,
                title: "contract.docx",
                icon: FileIcon,
                key: "contract"
            }
        ],
        meta: t("treeViewPage.items", [
            3
        ]),
        key: "documents"
    },
    {
        title: t("treeViewPage.folderImages"),
        children: [
            {
                meta: FILE_SIZES.cover,
                title: "cover.png",
                icon: FileIcon,
                key: "cover"
            },
            {
                meta: FILE_SIZES.logo,
                title: "logo.svg",
                icon: FileIcon,
                key: "logo"
            }
        ],
        meta: t("treeViewPage.items", [
            2
        ]),
        key: "images"
    },
    {
        title: t("treeViewPage.folderArchive"),
        meta: t("treeViewPage.locked"),
        isDisabled: true,
        key: "archive",
        children: []
    },
    {
        title: "notes.txt",
        icon: FileIcon,
        key: "notes"
    }
];

const getPermissionNodes = (t: DocsTranslate): Array<ITreeNode> => [
    {
        children: [
            {
                title: t("treeViewPage.projectMobile"),
                key: "mobile"
            },
            {
                title: t("treeViewPage.projectWebsite"),
                key: "website"
            },
            {
                title: t("treeViewPage.projectInternal"),
                isDisabled: true,
                key: "internal"
            }
        ],
        title: t("treeViewPage.groupProjects"),
        key: "projects"
    },
    {
        children: [
            {
                title: t("treeViewPage.reportMonthly"),
                key: "monthly"
            },
            {
                title: t("treeViewPage.reportYearly"),
                key: "yearly"
            }
        ],
        title: t("treeViewPage.groupReports"),
        key: "reports"
    }
];

const getSizeNodes = (t: DocsTranslate): Array<ITreeNode> => [
    {
        title: t("treeViewPage.folderDocuments"),
        children: [
            {
                title: "plan.md",
                icon: FileIcon,
                key: "plan"
            },
            {
                title: "memo.txt",
                icon: FileIcon,
                key: "memo"
            }
        ],
        key: "documents"
    },
    {
        title: "notes.txt",
        icon: FileIcon,
        key: "notes"
    }
];

const getDrillNodes = (t: DocsTranslate): Array<ITreeNode> => [
    {
        children: [
            {
                children: [
                    {
                        title: t("treeViewPage.folderMobile"),
                        children: [
                            {
                                title: "login.fig",
                                icon: FileIcon,
                                key: "login"
                            },
                            {
                                title: "home.fig",
                                icon: FileIcon,
                                key: "home"
                            }
                        ],
                        key: "mobile-screens"
                    },
                    {
                        title: t("treeViewPage.folderWeb"),
                        children: [
                            {
                                title: "dashboard.fig",
                                key: "dashboard",
                                icon: FileIcon
                            }
                        ],
                        key: "web-screens"
                    }
                ],
                title: t("treeViewPage.folderScreens"),
                key: "screens"
            },
            {
                title: t("treeViewPage.folderBrand"),
                children: [
                    {
                        key: "brand-logo",
                        title: "logo.svg",
                        icon: FileIcon
                    }
                ],
                key: "brand"
            },
            {
                key: "design-notes",
                title: "notes.txt",
                icon: FileIcon
            }
        ],
        title: t("treeViewPage.folderDesign"),
        key: "design"
    },
    {
        children: [
            {
                title: t("treeViewPage.folderSource"),
                children: [
                    {
                        title: "index.ts",
                        icon: FileIcon,
                        key: "index"
                    }
                ],
                key: "source"
            }
        ],
        title: t("treeViewPage.folderCode"),
        key: "code"
    }
];

const getLazyNodes = (t: DocsTranslate, loadedKeys: Array<string>): Array<ITreeNode> => [
    {
        children: [
            {
                children: loadedKeys.includes("design") ? [
                    {
                        title: "mockups.fig",
                        icon: FileIcon,
                        key: "mockups"
                    },
                    {
                        title: "components.fig",
                        key: "components",
                        icon: FileIcon
                    }
                ] : undefined,
                title: t("treeViewPage.folderDesign"),
                key: "design"
            },
            {
                children: loadedKeys.includes("backups") ? [
                    {
                        title: "backup-2026-10.zip",
                        icon: FileIcon,
                        key: "backup"
                    }
                ] : undefined,
                title: t("treeViewPage.folderBackups"),
                key: "backups"
            },
            {
                title: t("treeViewPage.groupReports"),
                key: "reports",
                children: []
            }
        ],
        title: t("treeViewPage.folderWorkspace"),
        key: "workspace"
    }
];

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("treeViewPage.selectionTitle"),
        id: "selection"
    },
    {
        title: t("treeViewPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("treeViewPage.lazyTitle"),
        id: "lazy"
    },
    {
        title: t("treeViewPage.menuTitle"),
        id: "menu"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TreeViewPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        expandedKeys,
        setExpandedKeys
    ] = useState<Array<string>>(INITIAL_EXPANDED);

    const [
        selectedKeys,
        setSelectedKeys
    ] = useState<Array<string>>(INITIAL_SELECTED);

    const [
        permissionKeys,
        setPermissionKeys
    ] = useState<Array<string>>([
        "website"
    ]);

    const [
        checkBehaviour,
        setCheckBehaviour
    ] = useState<"cascade" | "independent">("cascade");

    const [
        drillPath,
        setDrillPath
    ] = useState<Array<ITreeNode>>([]);

    const [
        loadedKeys,
        setLoadedKeys
    ] = useState<Array<string>>([]);

    const [
        lazyExpandedKeys,
        setLazyExpandedKeys
    ] = useState<Array<string>>(LAZY_ROOT);

    const [
        lastAction,
        setLastAction
    ] = useState("");

    const timersRef = useRef<Set<number>>(new Set());

    const attemptsRef = useRef(0);

    useEffect(() => {
        const timers = timersRef.current;

        return () => {
            timers.forEach((timer) => window.clearTimeout(timer));

            timers.clear();
        };
    }, []);

    const fileNodes = useMemo(() => getFileNodes(t), [
        t
    ]);

    const permissionNodes = useMemo(() => getPermissionNodes(t), [
        t
    ]);

    const sizeNodes = useMemo(() => getSizeNodes(t), [
        t
    ]);

    const drillNodes = useMemo(() => getDrillNodes(t), [
        t
    ]);

    const lazyNodes = useMemo(() => getLazyNodes(t, loadedKeys), [
        loadedKeys,
        t
    ]);

    const selectedNode = findNode(fileNodes, selectedKeys[0] ?? "");

    const permissionCount = permissionKeys.filter((key) => PERMISSION_LEAVES.includes(key)).length;

    const drillTitle = drillPath.length ? drillPath.map((node) => node.title).join(" › ") : t("treeViewPage.drillRoot");

    const handleExpand = (keys: Array<string>) => setExpandedKeys(keys);

    const handleSelect = (keys: Array<string>) => setSelectedKeys(keys);

    const handlePermissionSelect = (keys: Array<string>) => setPermissionKeys(keys);

    const handleBehaviourChange = (value: string | Array<string>) => setCheckBehaviour(value === "independent" ? "independent" : "cascade");

    const handleDrillChange = (path: Array<ITreeNode>) => setDrillPath(path);

    const handleLazyExpand = (keys: Array<string>) => setLazyExpandedKeys(keys);

    const handleNothing = () => undefined;

    const handleReset = () => {
        attemptsRef.current = 0;

        setLazyExpandedKeys(LAZY_ROOT);

        setLoadedKeys([]);
    };

    const handleLoadChildren = (node: ITreeNode) => new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => {
            timersRef.current.delete(timer);

            if(node.key === "backups" && attemptsRef.current === 0) {
                attemptsRef.current += 1;

                reject(new Error("failed"));

                return;
            }

            setLoadedKeys((current) => [
                ...current,
                node.key
            ]);

            resolve();
        }, LOAD_DELAY);

        timersRef.current.add(timer);
    });

    const renderItemMenu = (node: ITreeNode) => <DropdownMenu.Group>
        <DropdownMenu.Item
            onPress={() => setLastAction(`${t("treeViewPage.menuRename")} · ${node.title}`)}
            title={t("treeViewPage.menuRename")}
        />
        <DropdownMenu.Item
            onPress={() => setLastAction(`${t("treeViewPage.menuShare")} · ${node.title}`)}
            title={t("treeViewPage.menuShare")}
        />
        <DropdownMenu.Separator />
        <DropdownMenu.Item
            onPress={() => setLastAction(`${t("treeViewPage.menuDelete")} · ${node.title}`)}
            title={t("treeViewPage.menuDelete")}
            type="danger"
        />
    </DropdownMenu.Group>;

    const renderState = (label: string, tree: ReactNode) => <div
        style={COLUMN_STYLE}
        key={label}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {label}
        </Text>
        {tree}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("treeViewPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("treeViewPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("treeViewPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("treeViewPage.overviewCaption", [
                    selectedNode?.title ?? "—"
                ])}
                isStacked={true}
            >
                <TreeView
                    aria-label={t("treeViewPage.treeLabel")}
                    selectedKeys={selectedKeys}
                    expandedKeys={expandedKeys}
                    onExpand={handleExpand}
                    onSelect={handleSelect}
                    selectionMode="single"
                    isGuideVisible={true}
                    nodes={fileNodes}
                    variant="outline"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("treeViewPage.selectionTitle")}
            id="selection"
        >
            <Paragraph>
                {t("treeViewPage.selectionText")}
            </Paragraph>
            <Preview
                caption={t("treeViewPage.selectionCaption", [
                    permissionCount,
                    PERMISSION_LEAVES.length
                ])}
                isStacked={true}
            >
                <TreeView
                    aria-label={t("treeViewPage.permissionsLabel")}
                    onSelect={handlePermissionSelect}
                    checkBehaviour={checkBehaviour}
                    selectedKeys={permissionKeys}
                    selectionMode="multiple"
                    nodes={permissionNodes}
                    defaultExpandedKeys={[
                        "projects",
                        "reports"
                    ]}
                    isGuideVisible={true}
                    variant="outline"
                />
                <div
                    style={CONTROLS_STYLE}
                >
                    <ToggleGroup
                        title={t("treeViewPage.behaviourLabel")}
                        onValueChange={handleBehaviourChange}
                        attachBehaviour="attached"
                        value={checkBehaviour}
                        isClearEnabled={false}
                        variant="outline"
                        size="small"
                    >
                        <ToggleGroup.Item
                            title="cascade"
                            value="cascade"
                        />
                        <ToggleGroup.Item
                            title="independent"
                            value="independent"
                        />
                    </ToggleGroup>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("treeViewPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("treeViewPage.layoutText")}
            </Paragraph>
            <Preview>
                <div
                    style={GRID_STYLE}
                >
                    {
                        SIZES.map((size) => <div
                            style={COLUMN_STYLE}
                            key={size}
                        >
                            <InlineCode>
                                {`size="${size}"`}
                            </InlineCode>
                            <TreeView
                                aria-label={t("treeViewPage.treeLabel")}
                                defaultExpandedKeys={INITIAL_EXPANDED}
                                style={stylesheet.tree}
                                isGuideVisible={true}
                                nodes={sizeNodes}
                                size={size}
                            />
                        </div>)
                    }
                </div>
            </Preview>
            <Paragraph>
                {t("treeViewPage.drillText")}
            </Paragraph>
            <Preview
                caption={t("treeViewPage.drillCaption", [
                    drillTitle
                ])}
                isStacked={true}
            >
                <TreeView
                    aria-label={t("treeViewPage.treeLabel")}
                    onDrillChange={handleDrillChange}
                    layoutBehaviour="drillDown"
                    defaultExpandedKeys={[
                        "design"
                    ]}
                    isGuideVisible={true}
                    nodes={drillNodes}
                    maxIndentLevel={2}
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("treeViewPage.lazyTitle")}
            id="lazy"
        >
            <Paragraph>
                {t("treeViewPage.lazyText")}
            </Paragraph>
            <Preview
                caption={t("treeViewPage.lazyCaption")}
                isStacked={true}
            >
                <TreeView
                    aria-label={t("treeViewPage.treeLabel")}
                    onLoadChildren={handleLoadChildren}
                    expandedKeys={lazyExpandedKeys}
                    onExpand={handleLazyExpand}
                    isGuideVisible={true}
                    nodes={lazyNodes}
                    variant="outline"
                />
                <Button
                    title={t("treeViewPage.reset")}
                    onPress={handleReset}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={LAZY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("treeViewPage.menuTitle")}
            id="menu"
        >
            <Paragraph>
                {t("treeViewPage.menuText")}
            </Paragraph>
            <Preview
                caption={t("treeViewPage.menuCaption", [
                    lastAction || t("treeViewPage.noAction")
                ])}
                isStacked={true}
            >
                <TreeView
                    aria-label={t("treeViewPage.treeLabel")}
                    defaultExpandedKeys={INITIAL_EXPANDED}
                    renderItemMenu={renderItemMenu}
                    selectionMode="single"
                    isGuideVisible={true}
                    nodes={sizeNodes}
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("treeViewPage.statesText")}
            </Paragraph>
            <Preview>
                <div
                    style={GRID_STYLE}
                >
                    {
                        renderState(t("treeViewPage.stateLoading"), <TreeView
                            aria-label={t("treeViewPage.treeLabel")}
                            style={stylesheet.state}
                            isLoading={true}
                        />)
                    }
                    {
                        renderState(t("treeViewPage.stateEmpty"), <TreeView
                            aria-label={t("treeViewPage.treeLabel")}
                            style={stylesheet.state}
                            nodes={[]}
                        />)
                    }
                    {
                        renderState(t("treeViewPage.stateError"), <TreeView
                            aria-label={t("treeViewPage.treeLabel")}
                            error={t("treeViewPage.errorText")}
                            style={stylesheet.state}
                            onRetry={handleNothing}
                        />)
                    }
                    {
                        renderState(t("treeViewPage.stateDenied"), <TreeView
                            aria-label={t("treeViewPage.treeLabel")}
                            onRequestAccess={handleNothing}
                            isPermissionDenied={true}
                            style={stylesheet.state}
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("treeViewPage.a11yRoles"),
                    t("treeViewPage.a11yName"),
                    t("treeViewPage.a11yKeyboard"),
                    t("treeViewPage.a11yFocus"),
                    t("treeViewPage.a11yPointer"),
                    t("treeViewPage.a11yDisabled"),
                    t("treeViewPage.a11yAnnounce"),
                    t("treeViewPage.a11yNative")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("treeViewPage.propNodes"),
                        type: "Array<ITreeNode>",
                        defaultValue: "[]",
                        name: "nodes"
                    },
                    {
                        description: t("treeViewPage.propSelectionMode"),
                        type: "\"none\" | \"single\" | \"multiple\"",
                        defaultValue: "\"none\"",
                        name: "selectionMode"
                    },
                    {
                        description: t("treeViewPage.propCheckBehaviour"),
                        type: "\"cascade\" | \"independent\"",
                        defaultValue: "\"cascade\"",
                        name: "checkBehaviour"
                    },
                    {
                        description: t("treeViewPage.propSelectedKeys"),
                        type: "Array<string>",
                        name: "selectedKeys"
                    },
                    {
                        description: t("treeViewPage.propDefaultSelectedKeys"),
                        name: "defaultSelectedKeys",
                        type: "Array<string>",
                        defaultValue: "[]"
                    },
                    {
                        type: "(keys: Array<string>, node: ITreeNode) => void",
                        description: t("treeViewPage.propOnSelect"),
                        name: "onSelect"
                    },
                    {
                        description: t("treeViewPage.propExpandedKeys"),
                        type: "Array<string>",
                        name: "expandedKeys"
                    },
                    {
                        description: t("treeViewPage.propDefaultExpandedKeys"),
                        name: "defaultExpandedKeys",
                        type: "Array<string>",
                        defaultValue: "[]"
                    },
                    {
                        type: "(keys: Array<string>, node: ITreeNode) => void",
                        description: t("treeViewPage.propOnExpand"),
                        name: "onExpand"
                    },
                    {
                        description: t("treeViewPage.propOnPress"),
                        type: "(node: ITreeNode) => void",
                        name: "onPress"
                    },
                    {
                        description: t("treeViewPage.propOnLoadChildren"),
                        type: "(node: ITreeNode) => Promise<void>",
                        name: "onLoadChildren"
                    },
                    {
                        description: t("treeViewPage.propLayoutBehaviour"),
                        type: "\"auto\" | \"indent\" | \"drillDown\"",
                        defaultValue: "\"auto\"",
                        name: "layoutBehaviour"
                    },
                    {
                        description: t("treeViewPage.propMaxIndentLevel"),
                        name: "maxIndentLevel",
                        defaultValue: "3",
                        type: "number"
                    },
                    {
                        description: t("treeViewPage.propOnDrillChange"),
                        type: "(path: Array<ITreeNode>) => void",
                        name: "onDrillChange"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("treeViewPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("treeViewPage.propVariant"),
                        type: "\"plain\" | \"outline\"",
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        description: t("treeViewPage.propIsGuideVisible"),
                        name: "isGuideVisible",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("treeViewPage.propRenderItemMenu"),
                        type: "(node: ITreeNode) => ReactNode",
                        name: "renderItemMenu"
                    },
                    {
                        description: t("treeViewPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("treeViewPage.propError"),
                        type: "string | null",
                        defaultValue: "null",
                        name: "error"
                    },
                    {
                        description: t("treeViewPage.propOnRetry"),
                        type: "() => void",
                        name: "onRetry"
                    },
                    {
                        description: t("treeViewPage.propIsPermissionDenied"),
                        name: "isPermissionDenied",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("treeViewPage.propOnRequestAccess"),
                        name: "onRequestAccess",
                        type: "() => void"
                    },
                    {
                        description: t("treeViewPage.propEmptyContent"),
                        name: "emptyContent",
                        type: "ReactNode"
                    },
                    {
                        description: t("treeViewPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("treeViewPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("treeViewPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("treeViewPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("treeViewPage.nodeText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("treeViewPage.propNodeKey"),
                        type: "string",
                        name: "key"
                    },
                    {
                        description: t("treeViewPage.propNodeTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("treeViewPage.propNodeMeta"),
                        type: "string",
                        name: "meta"
                    },
                    {
                        description: t("treeViewPage.propNodeIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("treeViewPage.propNodeChildren"),
                        type: "Array<ITreeNode>",
                        name: "children"
                    },
                    {
                        description: t("treeViewPage.propNodeIsLeaf"),
                        name: "isLeaf",
                        type: "boolean"
                    },
                    {
                        description: t("treeViewPage.propNodeIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("treeViewPage.propNodeIsLoading"),
                        name: "isLoading",
                        type: "boolean"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TreeViewPage;
